
import { Donor } from '../types/Crossmatch'
import { WorkspaceCollectedData, WorkspaceHLAResult, WorkspaceHLAResults } from '../types/Sample'
import { isNullish } from './data'
import { ClassName } from '../types/shared/v1/ClassName'
import { HLASoftware } from '../types/shared/v1/Patient'
import { TypingGroup } from '../types/shared/v1/PatientTyping'
import { TypingType } from '../types/shared/v1/PatientTyping'
import { ConsolidatedPatientV2 } from '../types/shared/v2/Patient'
import { parseRound } from './math'
import { StoreObject } from './objects'
import { HLAResultDecision } from '../types/shared/v2/HLAResultDecision'
import { HLAResult, HLAResultRef, toHLAResultRef, toSpecificitiesKey } from '../types/shared/v2/HLAResult'
import { HLAEngineResult } from '../types/shared/v2/HLAEngineResult'
import { HLAEngineInterpretation } from '../types/shared/v2/HLAEngineResult'
import { Config } from '../config'
import { HLAEngineResultGroup, HLAEngineResultGroupType } from '../types/shared/v2/HLAEngineResultGroup'
import { Store } from 'redux'
import { areEquals } from './set';


export function flattenWorkspaceResults(results: WorkspaceHLAResults): { [key: string]: StoreObject<WorkspaceHLAResult> } {
  const workspace: { [key: string]: StoreObject<WorkspaceHLAResult> } = {}

  Object.keys(results.byClass).forEach(className => {
    Object.keys(results.byClass[className]).forEach(key => {
      workspace[`${className}-${key}`] = results.byClass[className][key]
    })
  })

  return workspace
}

export function getMaxMFI(result: WorkspaceHLAResult): any {
  let ordered = [...result.data]
  ordered = ordered.sort((a: WorkspaceCollectedData, b: WorkspaceCollectedData) => (a.normalValue !== null && b.normalValue !== null) ? parseRound(b.normalValue) - parseRound(a.normalValue) : -1)

  return parseRound(ordered[0].normalValue)
}

export type Interpretations = {
  [key in ClassName]: {
    [beadId: string]: InterpretationBead
  }
}

export interface InterpretationBead {
  allelic: string,
  generic: string;
  visible: boolean | undefined;
  synthesis?: StoreObject<HLAEngineResultGroup>;
}

export function getDefaultInterpretations(hlaResults: WorkspaceHLAResults, hlaEngineResult: StoreObject<HLAEngineResult>, hlaEngineResultGroups: StoreObject<HLAEngineResultGroup>[]): Interpretations {
  const interpretations: Interpretations = {
    [ClassName.CLASS1]: {},
    [ClassName.CLASS2]: {}
  }

  Object.keys(hlaResults.byClass).forEach((className: ClassName) => {
    Object.values(hlaResults.byClass[className]).forEach((result: StoreObject<WorkspaceHLAResult>) => {
      result.data.data.forEach((d) => {
        const specificitiesKey = toSpecificitiesKey(d.specificities)
        if (!specificitiesKey) return

        let best: StoreObject<HLAEngineResultGroup> | undefined;

        if (isNullish(interpretations[className][specificitiesKey])) {
          interpretations[className][specificitiesKey] = {
            allelic: specificitiesKey,
            generic: d.specAbBr,
            visible: d.visible
          }
        }

        const synthesisGroupCode = hlaEngineResult.data.beads[specificitiesKey]?.resultGroups?.synthesis
        if (!synthesisGroupCode) return;

        const synthesisGroup = hlaEngineResultGroups.find(g => g.data.type === HLAEngineResultGroupType.Synthesis && g.data.code === synthesisGroupCode)
        if (!synthesisGroup) return;

        interpretations[className][specificitiesKey].synthesis = synthesisGroup
      })
    })
  })

  return interpretations
}

export function getHistorisedSynthesis(interpretations: any): any {
  const thresholdIdToInlogId: any = {
    forbidden: 'AC1INT',
    neither: 'AC1ZG',
    allowed: 'AG1PER'
  }

  const thresholdIdToConcatInlogId: any = {
    neither: 'ACTZG',
    allowed: 'AGTPER'
  }

  const synthesis: any = {
    AC1INT: [],
    AC1ZG: [],
    ACTZG: [],
    AG1PER: [],
    AGTPER: []
  }

  const finalInterpretations: any = {}

  Object.keys(interpretations).forEach((className: any) => {
    Object.keys(interpretations[className]).forEach((key: any) => {
      if (isNullish(finalInterpretations[interpretations[className][key].generic])) {
        finalInterpretations[interpretations[className][key].generic] = interpretations[className][key].synthesis
      }

      if (!isNullish(finalInterpretations[interpretations[className][key].generic]) && finalInterpretations[interpretations[className][key].generic] !== interpretations[className][key].synthesis) {
        if (finalInterpretations[interpretations[className][key].generic] === 'allowed') {
          finalInterpretations[interpretations[className][key].generic] = interpretations[className][key].synthesis
        }

        if (finalInterpretations[interpretations[className][key].generic] === 'neither' && interpretations[className][key].synthesis === 'forbidden') {
          finalInterpretations[interpretations[className][key].generic] = interpretations[className][key].synthesis
        }
      }
    })
  })

  Object.keys(finalInterpretations).forEach((key: any) => {
    if (synthesis[thresholdIdToInlogId[finalInterpretations[key]]].includes(key) === false) {
      synthesis[thresholdIdToInlogId[finalInterpretations[key]]].push(key)
    }

    if (!isNullish(thresholdIdToConcatInlogId[finalInterpretations[key]]) && synthesis[thresholdIdToConcatInlogId[finalInterpretations[key]]].includes(key) === false) {
      synthesis[thresholdIdToConcatInlogId[finalInterpretations[key]]].push(key)
    }
  })

  return synthesis
}

export function getCrossmatchData(patient: ConsolidatedPatientV2, hlaResults: WorkspaceHLAResults, donor: Donor): any {
  const crossmatch = {}
  const flatDonorTyping: string[] = []
  const flatPatientTyping: string[] = []

  Object.keys(donor.typings).forEach((key) => {
    Object.values(donor.typings[key]).forEach((typing: string) => {
      flatDonorTyping.push(String(key + '*' + typing))
    })
  })

  Object.values(TypingGroup).forEach((group: string) => {
    Object.keys(patient.flatTypings[TypingType.TYPAGE_BM]).forEach((tKey) => {
      if (String(patient.flatTypings[TypingType.TYPAGE_BM][tKey]).startsWith(group)) {
        flatPatientTyping.push(String(patient.flatTypings[TypingType.TYPAGE_BM][tKey]))
      }
    })
  })

  flatDonorTyping.forEach((typing: string, index: number) => {
    let fill: boolean = false

    // Si la spé se termine par un tiret, elle est égale à la spé précédente
    const correctTyping = typing.endsWith('*') ? flatDonorTyping[index - 1] : typing

    // Si la spé est vide dans le typage du patient, on fait sortir la spé dans le tableau
    if (isNullish(flatPatientTyping[index])) {
      crossmatch[correctTyping] = {
        maxMFI: 0,
        date: new Date()
      }
      return
    }

    // On applique le même traitement pour les autres spé comparées que pour la spé du donneur
    const currentPatientTyping = flatPatientTyping[index].endsWith('*') ? flatPatientTyping[index - 1] : flatPatientTyping[index]

    // si la spé est différente de la spé à la même position dans le typage patient, on fait sortir la spé dans le tableau
    if (correctTyping !== currentPatientTyping) {
      fill = true
    }

    if (index % 2 === 0 && index < flatDonorTyping.length - 1) {
      const nextPatientTyping = flatPatientTyping[index + 1].endsWith('*') ? flatPatientTyping[index] : flatPatientTyping[index + 1]
      // si la spé est la première du locus, on la compare aussi à la 2ème du locus en face et si différente, on fait sortir la spé dans le tableau
      if (correctTyping !== nextPatientTyping) {
        fill = true
      }
    } else {
      const previousPatientTyping = flatPatientTyping[index - 1].endsWith('*') ? flatPatientTyping[index - 2] : flatPatientTyping[index - 1]
      // si la spé est la deuxième du locus, on la compare aussi à la 1ère du locus en face et si différente, on fait sortir la spé dans le tableau
      if (correctTyping !== previousPatientTyping) {
        fill = true
      }
    }

    if (fill) {
      crossmatch[correctTyping] = {
        maxMFI: 0,
        date: new Date()
      }
    }
  })

  Object.values(hlaResults.byClass[ClassName.CLASS1]).forEach((result: StoreObject<WorkspaceHLAResult>) => {
    result.data.data.forEach((d: WorkspaceCollectedData) => {
      const specificitiesKey = toSpecificitiesKey(d.specificities)
      if (Object.keys(crossmatch).includes(specificitiesKey)) {
        const value = parseRound(d.normalValue)
        crossmatch[specificitiesKey].maxMFI = isNullish(crossmatch[specificitiesKey].maxMFI) ? value : Math.max(crossmatch[specificitiesKey].maxMFI, value ?? 0)
        if (crossmatch[specificitiesKey].maxMFI === value) {
          crossmatch[specificitiesKey].date = result.data.ref.analysisRequest.requestDate
        }
      }
    })
  })

  return crossmatch
}


const AUTO_COMMENT_SECTION_START = "[AUTO-TRANSCODING]"
const AUTO_COMMENT_SECTION_END = "[/AUTO-TRANSCODING]"

/**
 * Extrait le contenu de la section auto-générée (allèles non transcodables / non
 * représentés par le panel anticorps) du commentaire de classe, le cas échéant.
 */
export function extractAutoTranscodingComment(comment?: string): string | undefined {
  if (!comment) return undefined

  const startIdx = comment.indexOf(AUTO_COMMENT_SECTION_START)
  const endIdx = comment.indexOf(AUTO_COMMENT_SECTION_END)
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return undefined

  return comment.slice(startIdx + AUTO_COMMENT_SECTION_START.length, endIdx).trim()
}

export function matchHLAResultRef(hlaResult: StoreObject<HLAResult>, ref: HLAResultRef) {
  return areHLAResultRefEquals(toHLAResultRef(hlaResult.data), ref)
}

export function areHLAResultRefEquals(ref1: HLAResultRef, ref2: HLAResultRef) {
  return ref1.batchDate === ref2.batchDate
    && ref1.catalogId === ref2.catalogId
    && ref1.class === ref2.class
    && ref1.origin === ref2.origin
    && ref1.sample === ref2.sample
    // Ticket #881 - Deux intégrations d'un même prélèvement sur une même plaque
    // ne diffèrent que par ce compteur. Sans lui, la synthèse ou la décision de
    // l'une se rattacherait indifféremment à l'autre. Un résultat importé d'un
    // fichier antérieur au #822 porte zéro, pas d'absence.
    && (ref1.dilutionFactor ?? 0) === (ref2.dilutionFactor ?? 0)
}

// Ticket #881 - Référence d'un résultat qui n'est pas encore chargé. Ses
// valeurs ne désignent aucun résultat, ce qui est l'effet voulu : les
// sélecteurs ne trouvent rien tant que la donnée n'est pas là.
export const EmptyHLAResultRef: HLAResultRef = {
  batchDate: "",
  class: "" as ClassName,
  catalogId: "",
  origin: "" as HLASoftware,
  sample: "",
  dilutionFactor: 0,
}

// toOptionalHLAResultRef construit la référence d'un résultat éventuellement
// absent.
//
// Les composants la bâtissaient champ par champ, avec une valeur de repli par
// champ. Chacun était une occasion d'oublier un champ de la clé, et le numéro
// d'intégration a été oublié partout : la comparaison étant devenue stricte,
// une seconde intégration ne retrouvait plus ni sa synthèse, ni sa décision,
// ni sa trace de moteur de règles.
export function toOptionalHLAResultRef(hlaResult?: StoreObject<HLAResult>): HLAResultRef {
  return hlaResult ? toHLAResultRef(hlaResult.data) : EmptyHLAResultRef
}
